import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Chart, CARD_W, GAP, MAX_BRANCH_COLS, MAX_LEAF_COLS, MIN_SCALE } from "./OrgChart.styles";
import { routeCrossLinks } from "./crossLinkRouting";

/**
 * @fileoverview OrgChart — top-down, view-only tree, expanded by default.
 * Width is controlled by structure, not shrinking: branch children (people with
 * reports) occupy columns; leaf children cluster into a compact block (max 4
 * wide) so a parent with 40 data owners costs 4 columns, not 40; the column
 * budget is capped so wide and narrow screens render the same compact tree,
 * centred. Scaling remains only as a last-resort backstop for extreme trees.
 *
 * Each person is drawn exactly once. Their remaining valid parents arrive as
 * `alsoParents` and are drawn as dashed cross-links by an SVG overlay: the tree
 * is a flex layout, so the endpoints are measured from the DOM and recomputed
 * whenever the layout changes (expand/collapse, resize, column budget, scale).
 * Those links are routed subway-style — out of a card's side, down a corridor
 * chosen to clear every card, along its own lane in a gutter reserved beneath
 * the tree, then back up into the target's side — so they never overlap a card
 * or each other, and the solid hierarchy stays the only thing running top-down.
 */

const EMPTY_NODE = { id: "root", userId: null, name: "", role: "", children: [] };

const ROLE = {
  ADMIN: { accent: "#2f4a6b", border: "#2f4a6b" },   /* deep navy  */
  OWNER: { accent: "#8ba0b4", border: "#d3dde6" },   /* slate grey */
};

/* internal auditors: cohesive cool ramp, one step per level */
const AUDITOR_LEVEL_COLORS = [
  { accent: "#2f9c9c", border: "#bfe0e0" }, /* L1 teal   */
  { accent: "#3a78ad", border: "#bcd5e8" }, /* L2 blue   */
  { accent: "#4a55a0", border: "#c6cae9" }, /* L3 indigo */
  { accent: "#6a4f9e", border: "#d0c4e6" }, /* L4 violet */
];

/* external auditors: warm rose/magenta ramp, distinct from internal */
const EXTERNAL_LEVEL_COLORS = [
  { accent: "#b5548a", border: "#e6c0d6" }, /* L1 magenta */
  { accent: "#a24d6f", border: "#e0bccb" }, /* L2 rose    */
];

function roleMeta(role = "") {
  const r = role.toLowerCase();
  if (r.includes("data owner")) return ROLE.OWNER;
  if (r.includes("admin")) return ROLE.ADMIN;
  if (r.includes("auditor")) {
    const m = r.match(/l\s*(\d+)/);
    const lvl = m ? parseInt(m[1], 10) : 1;
    const palette = r.includes("external") ? EXTERNAL_LEVEL_COLORS : AUDITOR_LEVEL_COLORS;
    return palette[(lvl - 1 + palette.length) % palette.length];
  }
  return ROLE.OWNER;
}

const kids = (n) => (Array.isArray(n?.children) ? n.children : []);
const hasKids = (n) => kids(n).length > 0;
const heads = (n) => (Array.isArray(n?.heads) ? n.heads : []);
const extraParents = (n) => (Array.isArray(n?.alsoParents) ? n.alsoParents : []);

/**
 * Lets every rendered Card hand its DOM element to the chart, keyed by userId,
 * so cross-link endpoints can be measured without threading refs through the
 * recursive markup. Null when no overlay is in play (Cards then skip it).
 */
const CardRegistry = React.createContext(null);

/** visit a node and every person beneath it — both group heads and children. */
function walkAll(node, fn) {
  if (!node) return;
  fn(node);
  heads(node).forEach((h) => walkAll(h, fn));
  kids(node).forEach((c) => walkAll(c, fn));
}

/** people in a node's subtree (counts group heads as people, not the container). */
function countAll(n) {
  let c = 0;
  kids(n).forEach((child) => {
    c += child.group ? heads(child).length : 1;
    c += countAll(child);
  });
  return c;
}

/** distinct roles present (real people, incl. group heads) — for the legend. */
function collectRoles(node) {
  const seen = new Map();
  walkAll(node, (n) => {
    if (n.userId != null && n.role && !seen.has(n.role)) seen.set(n.role, roleMeta(n.role));
  });
  return seen;
}

/** ids of collapsible nodes (regular nodes with children; groups never collapse). */
function collectBranchIds(node) {
  const out = [];
  walkAll(node, (n) => { if (!n.group && n.userId != null && kids(n).length) out.push(n.id); });
  return out;
}

/** rows of an even width, so the link between rows falls in a gutter */
function chunk(list, cols) {
  if (list.length <= cols) return [list];
  const n = Math.max(2, cols % 2 === 0 ? cols : cols - 1);
  const rows = [];
  for (let i = 0; i < list.length; i += n) rows.push(list.slice(i, i + n));
  return rows;
}

const Chevron = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M1.5 3.5 5 7l3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** one row of slots, with the bus segments between them */
function Row({ items, render }) {
  const solo = items.length === 1;
  return (
    <div className="ocx-row">
      {items.map((item, i) => (
        <div className="ocx-slot" key={item.key}>
          {!solo && i > 0 && <span className="ocx-bus-l" />}
          {!solo && i < items.length - 1 && <span className="ocx-bus-r" />}
          {render(item)}
        </div>
      ))}
    </div>
  );
}

/** terminal children, packed into a compact block instead of one column each */
function LeafCluster({ leaves, cols }) {
  const rows = chunk(leaves, cols);
  return (
    <div className="ocx-cluster">
      {rows.map((row, r) => (
        <React.Fragment key={r}>
          {r > 0 && <div className="ocx-link" />}
          <Row
            items={row.map((n) => ({ key: n.id, node: n }))}
            render={({ node }) => <Card node={node} />}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * @param {{ node: object, root?: boolean, open?: boolean, onToggle?: () => void }} props
 */
function Card({ node, root, open, onToggle }) {
  const meta = roleMeta(node.role);
  const branch = hasKids(node);
  const register = useContext(CardRegistry);
  const { userId } = node;
  const shared = extraParents(node);

  const bindCard = useCallback(
    (el) => { if (register && userId != null) register(userId, el); },
    [register, userId],
  );

  const label = shared.length
    ? `${node.name} — ${node.role} · also reports to ${shared.map((p) => p.name).join(", ")}`
    : `${node.name} — ${node.role}`;

  return (
    <div
      ref={bindCard}
      className={`ocx-card${root ? " is-root" : ""}`}
      style={{ "--bd": meta.border, "--ac": meta.accent }}
      title={label}
    >
      <div className="ocx-name">{node.name}</div>
      <div className="ocx-role">{node.role}</div>
      {branch && onToggle && (
        <button
          className="ocx-pill"
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${node.name}`}
          onClick={onToggle}
        >
          <Chevron />
          {countAll(node)}
        </button>
      )}
    </div>
  );
}

/** render a child node — a co-head group or a regular node. */
function renderNode(node, ctx) {
  return node.group ? <CoHeadGroup node={node} {...ctx} /> : <Node node={node} {...ctx} />;
}

/** a person card plus, when expanded, their reports split into columns + a leaf cluster. */
function Node({ node, openSet, toggle, branchCols, leafCols }) {
  const branch = hasKids(node);
  const open = branch && openSet.has(node.id);
  const ctx = { openSet, toggle, branchCols, leafCols };

  const { columns, leaves } = useMemo(() => {
    const cols = [];
    const lvs = [];
    kids(node).forEach((child) => {
      (hasKids(child) || child.group ? cols : lvs).push(child);
    });
    return { columns: cols, leaves: lvs };
  }, [node]);

  const slots = useMemo(() => {
    const list = columns.map((c) => ({ type: "node", node: c, key: c.id }));
    if (leaves.length) list.push({ type: "cluster", key: `${node.id}:leaves`, leaves });
    return list;
  }, [columns, leaves, node.id]);

  const rows = open ? chunk(slots, branchCols) : [];

  return (
    <div className="ocx-node">
      <Card node={node} open={open} onToggle={branch ? () => toggle(node.id) : undefined} />

      {open && (
        <div className="ocx-kids">
          {rows.map((row, r) => (
            <React.Fragment key={r}>
              {r > 0 && <div className="ocx-link" />}
              <Row
                items={row}
                render={(slot) =>
                  slot.type === "cluster"
                    ? <LeafCluster leaves={slot.leaves} cols={leafCols} />
                    : renderNode(slot.node, ctx)
                }
              />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/** co-head group: the merged people sit side by side over one shared subtree. */
function CoHeadGroup({ node, openSet, toggle, branchCols, leafCols }) {
  const ctx = { openSet, toggle, branchCols, leafCols };
  const groupHeads = heads(node);
  const children = kids(node);
  const single = groupHeads.length <= 1;

  return (
    <div className="ocx-co">
      <div className="ocx-co-heads">
        {groupHeads.map((h, i) => (
          <div className="ocx-co-slot" key={h.id ?? i}>
            {!single && i > 0 && <span className="ocx-co-busl" />}
            {!single && i < groupHeads.length - 1 && <span className="ocx-co-busr" />}
            <Card node={h} />
          </div>
        ))}
      </div>
      {children.length > 0 && <div className="ocx-co-trunk" />}
      {children.length > 0 && (
        <Row
          items={children.map((c) => ({ key: c.id, node: c }))}
          render={(slot) => renderNode(slot.node, ctx)}
        />
      )}
    </div>
  );
}

/** the dashed edges to draw: every `alsoParents` entry, flattened. */
function collectCrossLinks(node) {
  const out = [];
  walkAll(node, (n) => {
    if (n.userId == null) return;
    extraParents(n).forEach((p) => {
      out.push({
        key: `${p.userId}->${n.userId}`,
        from: p.userId,
        to: n.userId,
        label: `${n.name} also reports to ${p.name}`,
      });
    });
  });
  return out;
}

export default function OrgChart({ data = EMPTY_NODE }) {
  const allBranchIds = useMemo(() => collectBranchIds(data), [data]);
  const crossLinks = useMemo(() => collectCrossLinks(data), [data]);

  const [openSet, setOpenSet] = useState(() => new Set(allBranchIds));
  const [cols, setCols] = useState({ branch: 3, leaf: 4 });
  const [box, setBox] = useState({ scale: 1, left: 0, height: 0 });
  const [links, setLinks] = useState({ w: 0, h: 0, items: [] });

  const stageRef = useRef(null);
  const treeRef = useRef(null);
  const cardsRef = useRef(new Map());

  const registerCard = useCallback((userId, el) => {
    if (el) cardsRef.current.set(userId, el);
    else cardsRef.current.delete(userId);
  }, []);

  useEffect(() => setOpenSet(new Set(allBranchIds)), [allBranchIds]);

  const toggle = useCallback((id) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const total = useMemo(() => {
    const ids = new Set();
    walkAll(data, (n) => { if (n.userId != null) ids.add(n.userId); });
    return ids.size;
  }, [data]);
  const legend = useMemo(() => Array.from(collectRoles(data).entries()), [data]);

  useLayoutEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      const tree = treeRef.current;
      if (!stage || !tree) return;

      const avail = stage.clientWidth;
      if (!avail) return;

      const fits = Math.max(2, Math.floor((avail + GAP) / (CARD_W + GAP)));
      const next = {
        branch: Math.min(fits, MAX_BRANCH_COLS),
        leaf: Math.min(fits, MAX_LEAF_COLS),
      };
      setCols((c) =>
        c.branch === next.branch && c.leaf === next.leaf ? c : next
      );

      const w = tree.scrollWidth;
      const h = tree.offsetHeight;
      const scale = w > avail ? Math.max(MIN_SCALE, avail / w) : 1;

      const treeRect = tree.getBoundingClientRect();
      const rel = (el) => {
        const r = el.getBoundingClientRect();
        return {
          x: (r.left - treeRect.left) / scale,
          y: (r.top - treeRect.top) / scale,
          w: r.width / scale,
          h: r.height / scale,
        };
      };

      const rectByUser = new Map();
      cardsRef.current.forEach((el, userId) => rectByUser.set(userId, rel(el)));
      const { items, gutter } = routeCrossLinks(
        crossLinks,
        (userId) => rectByUser.get(userId) || null,
        Array.from(rectByUser.values()),
        h,
      );

      setLinks((prev) => {
        const next = { w, h: h + gutter, items };
        const same =
          prev.w === next.w &&
          prev.h === next.h &&
          prev.items.length === items.length &&
          prev.items.every((it, i) => it.key === items[i].key && it.d === items[i].d);
        return same ? prev : next;
      });

      setBox((prev) => {
        const nextBox = {
          scale,
          left: Math.max(0, (avail - w * scale) / 2),
          height: (h + gutter) * scale,
        };
        const same =
          Math.abs(prev.scale - nextBox.scale) < 0.005 &&
          Math.abs(prev.left - nextBox.left) < 1 &&
          Math.abs(prev.height - nextBox.height) < 1;
        return same ? prev : nextBox;
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (stageRef.current) ro.observe(stageRef.current);
    if (treeRef.current) ro.observe(treeRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [data, openSet, cols, crossLinks]);

  return (
    <Chart>
      <div className="ocx-bar">
        <span className="ocx-tally">{total} people</span>
      </div>

      <div className="ocx-stage" ref={stageRef} style={{ height: box.height || undefined }}>
        <div className="ocx-fit" style={{ transform: `scale(${box.scale})`, left: box.left }}>
          <CardRegistry.Provider value={registerCard}>
            <div className="ocx-tree" ref={treeRef}>
              {links.items.length > 0 && (
                <svg className="ocx-xlinks" width={links.w} height={links.h} focusable="false">
                  <defs>
                    <marker
                      id="ocx-xarrow"
                      markerWidth="7"
                      markerHeight="7"
                      refX="6"
                      refY="3.5"
                      orient="auto"
                    >
                      <path d="M0 0.5 L6.5 3.5 L0 6.5 z" />
                    </marker>
                  </defs>
                  {links.items.map((l) => (
                    <path key={l.key} d={l.d} markerEnd="url(#ocx-xarrow)">
                      <title>{l.label}</title>
                    </path>
                  ))}
                </svg>
              )}
              {(data.userId == null ? kids(data) : [data]).map((top) => (
                <React.Fragment key={top.id}>
                  {renderNode(top, { openSet, toggle, branchCols: cols.branch, leafCols: cols.leaf })}
                </React.Fragment>
              ))}
            </div>
          </CardRegistry.Provider>
        </div>
      </div>

      <div className="ocx-legend">
        {legend.map(([role, meta]) => (
          <span key={role}><i style={{ background: meta.accent }} />{role}</span>
        ))}
        {crossLinks.length > 0 && (
          <span><i className="ocx-dash" />Also reports to (shared KPI)</span>
        )}
      </div>
    </Chart>
  );
}

